import * as React from 'react';
import { cn } from '../../utils/cn';

/**
 * Intrinsic aspect ratio of the wordmark (viewBox width / height ≈ 4.4:1).
 * Exported so callers that need the geometry (e.g. laying the logo out in an
 * exported image) can derive a width from a chosen height without re-measuring.
 */
export const SPATE_LOGO_ASPECT_RATIO = 79.26 / 17.99;

/**
 * SpateLogo — the Spate "SPATE" wordmark, as an inline SVG.
 *
 * Fill is `currentColor`, so the logo inherits the surrounding text color. Set
 * the color with a Tailwind `text-*` class on `className` (e.g.
 * `text-foreground`, `text-white`) rather than hardcoding a hex.
 *
 * ## Sizing
 *
 * The wordmark is sized by HEIGHT; the width is derived from the intrinsic
 * aspect ratio (≈4.4:1) so the logo never distorts. **Resize via the `height`
 * prop, not a Tailwind height class** — `className` is for color and layout,
 * the same convention as `SpateAiIcon`'s `size` prop.
 *
 * Keep masthead / navigation usages at the app's established size so the logo
 * stays visually consistent everywhere it appears:
 *
 * - **Masthead / navigation rail: `height={20}` (the default).** This mirrors
 *   the existing `AppSidebar` header, which renders the wordmark at
 *   `h-[1.25rem]` (== `h-5` == 20px). Anywhere the logo sits in a top masthead
 *   or a nav sidebar, use the default height.
 *
 * Other contexts (marketing/empty-state headers, onboarding, splash) can scale
 * up freely — e.g. `height={32}` or `height={48}` — but nav/masthead stays 20px.
 *
 * @param {object} props
 * @param {number} [props.height=20] Rendered height in px; width follows the ratio.
 * @param {string} [props.title='Spate'] Accessible name (rendered as <title>).
 * @param {string} [props.className] Tailwind classes for color/layout.
 */
const SpateLogo = React.forwardRef(
    ({ className, height = 20, title = 'Spate', ...props }, ref) => (
        <svg
            ref={ref}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="26.67 57.31 79.26 17.99"
            height={height}
            width={height * SPATE_LOGO_ASPECT_RATIO}
            role="img"
            fill="currentColor"
            className={cn('shrink-0', className)}
            {...props}
        >
            <title>{title}</title>
            <path d="M33.41,75.3c-1.34,0-2.62-.21-3.85-.62-1.22-.41-2.19-.94-2.89-1.6l.93-1.96c.67.59,1.52,1.07,2.56,1.46,1.04.39,2.12.58,3.24.58,1.02,0,1.85-.12,2.49-.35.64-.23,1.11-.56,1.41-.97.3-.41.45-.88.45-1.39,0-.6-.2-1.09-.59-1.46-.39-.37-.9-.67-1.53-.89-.63-.23-1.32-.42-2.07-.59s-1.51-.36-2.27-.59c-.76-.23-1.46-.52-2.09-.87-.63-.35-1.14-.83-1.52-1.42-.39-.59-.58-1.36-.58-2.3s.24-1.74.72-2.5c.48-.76,1.21-1.37,2.21-1.83,1-.46,2.27-.69,3.81-.69,1.02,0,2.04.13,3.04.4,1,.27,1.88.65,2.61,1.16l-.83,2.01c-.75-.5-1.55-.87-2.39-1.09-.84-.23-1.65-.34-2.44-.34-.99,0-1.8.13-2.44.38-.64.25-1.1.59-1.39,1-.29.42-.44.89-.44,1.41,0,.62.2,1.11.59,1.48.39.37.9.66,1.53.88s1.32.41,2.07.59,1.51.37,2.27.59c.76.22,1.46.5,2.09.85.63.35,1.13.82,1.52,1.41.38.59.58,1.34.58,2.26s-.24,1.71-.73,2.48c-.49.76-1.24,1.37-2.25,1.83-1.01.46-2.29.69-3.83.69Z" />
            <path d="M43.89,75.1v-17.59h6.86c1.54,0,2.86.24,3.95.73,1.09.49,1.93,1.19,2.51,2.11.59.92.88,2.02.88,3.29s-.29,2.37-.88,3.28c-.59.91-1.42,1.62-2.51,2.11-1.09.49-2.4.74-3.95.74h-5.48l1.13-1.18v6.51h-2.51ZM46.4,68.84l-1.13-1.26h5.4c1.61,0,2.83-.34,3.66-1.03.83-.69,1.24-1.66,1.24-2.92s-.41-2.23-1.24-2.92c-.83-.69-2.05-1.03-3.66-1.03h-5.4l1.13-1.26v10.4Z" />
            <path d="M58.24,75.1l7.97-17.59h2.49l7.99,17.59h-2.64l-7.11-16.18h1l-7.11,16.18h-2.59ZM61.63,70.7l.68-2.01h9.9l.73,2.01h-11.31Z" />
            <path d="M81.91,75.1v-15.41h-6.03v-2.19h14.55v2.19h-6.03v15.41h-2.49Z" />
            <path d="M95.68,72.91h10.25v2.19h-12.77v-17.59h12.42v2.19h-9.9v13.22ZM95.46,65.1h9.05v2.14h-9.05v-2.14Z" />
        </svg>
    ),
);
SpateLogo.displayName = 'SpateLogo';

export { SpateLogo };
